/**
 * Thanksgiving tee designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingTeeDesignsPack: ToilePack = {
    slug: 'thanksgiving-tee-designs',
    category: 'season',
    name: 'Thanksgiving tee designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'slogan', label: 'Slogan', subject: 'The words "THANKFUL & BLESSED" lettered in bold retro type with a wheat sprig, isolated on a plain ground'},
        {key: 'turkey', label: 'Cartoon turkey', subject: 'A cartoon turkey with a fanned tail and a pilgrim hat, isolated on a plain ground, no text'},
        {key: 'badge', label: 'Retro badge', subject: 'A maple leaf in a retro sunburst circle with a distressed texture, isolated, no text'},
    ],
    styles: [
        {key: 'harvest', label: 'Harvest flat', style: 'flat harvest illustration, simplified shapes, no outlines, matte colour', palette: 'Mustard, rust, pumpkin orange, olive and cream', framing: 'Centered on a solid cream background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage americana', style: 'vintage americana illustration, halftone grain, slight misregistration, warm', palette: 'Faded rust, brown, mustard and cream', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolour illustration, loose edges, paper texture showing', palette: 'Warm autumn washes of orange, ochre and brown', framing: 'Centered with white paper around it'},
    ],
    directions: ['a matching kids size', 'with a pilgrim hat added', 'in two colors only'],
    directionHint: 'try: a matching kids size',
    seo: {
        title: 'Thanksgiving Tee Designs: Slogan, Turkey & Badge',
        metaDescription: 'Nine Thanksgiving t-shirt designs — Thankful & Blessed slogan, cartoon turkey, retro badge — in harvest flat, vintage, and watercolor styles.',
        h1: 'Thanksgiving t-shirt design: slogan, cartoon turkey, retro badge',
        tagline: 'Nine designs, three graphics, three treatments each.',
        intro: 'Nine Thanksgiving designs for the chest, isolated on a plain ground with no shirt shown: a Thankful & Blessed slogan with a wheat sprig, a cartoon turkey in a pilgrim hat, and a retro maple-leaf sunburst badge, each in a harvest flat, a vintage americana, and a watercolor treatment. Ready to print on any garment.',
        useCases: [
            'a print-on-demand t-shirt listing',
            'a family Thanksgiving matching shirt',
            'a heat-transfer design for a home printer',
            'a tote bag or apron graphic',
        ],
        faq: [
            {q: 'Is the shirt included, or just the design?', a: 'Just the design — each file is the artwork alone on a plain background, ready to place onto any shirt, tote, or apron in a mockup tool or print service.'},
            {q: 'Can I use this for print-on-demand?', a: 'Yes — the designs are cleared for commercial use, including print-on-demand storefronts.'},
            {q: "What's the difference between the three styles?", a: 'Harvest flat uses simplified matte shapes, vintage americana adds halftone grain and warm faded color, and watercolor uses soft loose edges with visible paper texture.'},
            {q: 'Does the slogan design come with real, legible text?', a: "Yes — 'Thankful & Blessed' is lettered directly into the design, spelled out clean and legible, not placeholder text."},
        ],
    },
}
